import {
  Command,
  durationString,
  RecommendedUser,
  timeRangeDurationString,
} from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React from 'react'
import { View } from 'react-native'
import { CommandButton } from 'shared/components/command_button'
import { UserAvatar } from 'shared/components/user/user_avatar'

export const WEEKLY_HOURS_LIMIT = 43
export const DAILY_HOURS_LIMIT = 8

interface Props {
  users?: RecommendedUser[]
  onButtonPress(command: Command, note?: string): Promise<void>
}

export function RecommendedUserList({ users, onButtonPress }: Props) {
  if (!users) {
    return null
  }
  return (
    <>
      {users.map(user => (
        <RecommendedUserCard
          key={user.userId}
          user={user}
          onButtonPress={onButtonPress}
        />
      ))}
    </>
  )
}

interface RecommendedUserCardProps {
  user: RecommendedUser
  onButtonPress(command: Command, note?: string): Promise<void>
}

function RecommendedUserCard({
  user,
  onButtonPress,
}: RecommendedUserCardProps) {
  const styles = useStyles(user)
  return (
    <View key={user.userId} style={styles.card}>
      <View style={styles.userContainer}>
        <View style={styles.container}>
          <UserAvatar user={user} size={'medium'} showAvatarIfNoPhone />
        </View>
        <View style={styles.userDetailsContainer}>
          <Text
            style={styles.name}
          >{`${user.firstName} ${user.lastName}`}</Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.secondaryText}>{`${t('week')}: `}</Text>
            <Text style={styles.weekHoursCountText}>
              {durationString(user.weekHours)}
            </Text>
            <Text style={[styles.secondaryText, styles.dayHoursText]}>{`${t(
              'day'
            )}: `}</Text>
            <Text style={styles.dayHoursCountText}>
              {durationString(user.dayHours)}
            </Text>
          </View>
        </View>
        <View style={styles.container}>
          <CommandButton command={user.commands[0]} onPress={onButtonPress} />
        </View>
      </View>
      <View style={styles.timeRangeDurationRow}>
        <Text style={styles.timeRangeDurationText}>{`${t(
          'working'
        )}: ${timeRangeDurationString(
          user.matchedShift.startTime,
          user.matchedShift.endTime,
          user.matchedShift.duration
        )}`}</Text>
        {user.timeAdjustNote && (
          <Text style={styles.timeAdjustText}>{user.timeAdjustNote}</Text>
        )}
      </View>
    </View>
  )
}

const useStyles = makeStyles((theme, user: RecommendedUser) => ({
  container: { margin: 10 },
  card: {
    justifyContent: 'center',
    backgroundColor: theme.colors?.white,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    marginBottom: 17,
  },
  name: {
    fontFamily: font.bold,
    fontSize: 19,
  },
  secondaryText: { color: theme.colors.grey3 },
  timeRangeDurationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 10,
    marginBottom: 10,
  },
  timeRangeDurationText: { marginHorizontal: 5, color: theme.colors.grey2 },
  weekHoursCountText: {
    color:
      user.weekHours > WEEKLY_HOURS_LIMIT
        ? theme.colors.primary
        : theme.colors.grey3,
  },
  dayHoursCountText: {
    color:
      user.dayHours > DAILY_HOURS_LIMIT
        ? theme.colors.primary
        : theme.colors.grey3,
  },
  dayHoursText: {
    marginLeft: 15,
    color: theme.colors.grey3,
  },
  userDetailsContainer: { margin: 10, flex: 1 },
  userContainer: { flexDirection: 'row', alignItems: 'center' },
  timeAdjustText: {
    color: user.matchType === 'GAP' ? theme.colors.grey1 : theme.colors.primary,
    fontWeight: user.matchType === 'GAP' ? 'bold' : 'normal',
  },
}))
